import { Buffer, BufferUsage, Container, Geometry, Mesh, Shader } from 'pixi.js';

/**
 * Transient effects: expanding rings and soft bursts.
 *
 * All of them live in one buffer and go out in a single additive draw call. The
 * alternative — a Graphics per effect, cleared and redrawn every frame — turns a
 * celebration into a spike of geometry rebuilds at exactly the moment the frame is
 * already busy.
 *
 * Because they are additive and bright, the bloom pass picks them up for free, which is
 * most of why they read as light rather than as decals.
 */

export type EffectKind = 'ring' | 'burst';

/** Hard cap on simultaneous effects. Oldest is recycled when full. */
const CAPACITY = 32;

/** Floats per vertex: position (2), corner (2), colour (3), progress (1), thickness (1). */
const VERTEX_FLOATS = 9;

const vertex = /* glsl */ `#version 300 es
in vec2 aPosition;
in vec2 aCorner;
in vec3 aColor;
in float aProgress;
in float aThickness;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vCorner;
out vec3 vColor;
out float vProgress;
out float vThickness;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vCorner = aCorner;
    vColor = aColor;
    vProgress = aProgress;
    vThickness = aThickness;
}
`;

const fragment = /* glsl */ `#version 300 es
precision mediump float;

in vec2 vCorner;
in vec3 vColor;
in float vProgress;
in float vThickness;
out vec4 fragColor;

void main() {
    float radius = length(vCorner);
    if (radius > 1.0) discard;

    // Negative thickness marks a burst; anything else is a ring. Packing the kind into a
    // sign keeps the vertex format to one float per attribute.
    float alpha;
    if (vThickness < 0.0) {
        float falloff = 1.0 - radius;
        alpha = falloff * falloff * (1.0 - vProgress);
    } else {
        // The ring's radius is its progress, so it sweeps outwards over its lifetime.
        float distance = abs(radius - vProgress);
        alpha = smoothstep(vThickness, 0.0, distance) * (1.0 - vProgress);
    }

    if (alpha <= 0.002) discard;
    // Additive: no alpha channel to respect, the colour is the contribution.
    fragColor = vec4(vColor * alpha, alpha);
}
`;

interface Effect {
  x: number;
  y: number;
  radius: number;
  age: number;
  life: number;
  thickness: number;
  r: number;
  g: number;
  b: number;
  active: boolean;
}

export class EffectsRenderer {
  readonly container = new Container({ label: 'effects' });

  private readonly effects: Effect[] = [];
  private readonly vertices: Float32Array;
  private readonly vertexBuffer: Buffer;
  private next = 0;

  constructor() {
    for (let i = 0; i < CAPACITY; i++) {
      this.effects.push({
        x: 0,
        y: 0,
        radius: 0,
        age: 0,
        life: 1,
        thickness: 0.2,
        r: 1,
        g: 1,
        b: 1,
        active: false,
      });
    }

    this.vertices = new Float32Array(CAPACITY * 4 * VERTEX_FLOATS);
    this.vertexBuffer = new Buffer({
      data: this.vertices,
      usage: BufferUsage.VERTEX | BufferUsage.COPY_DST,
    });

    const indices = new Uint32Array(CAPACITY * 6);
    for (let i = 0; i < CAPACITY; i++) {
      const v = i * 4;
      const o = i * 6;
      indices[o] = v;
      indices[o + 1] = v + 1;
      indices[o + 2] = v + 2;
      indices[o + 3] = v;
      indices[o + 4] = v + 2;
      indices[o + 5] = v + 3;
    }

    const stride = VERTEX_FLOATS * 4;
    const geometry = new Geometry({
      attributes: {
        aPosition: { buffer: this.vertexBuffer, format: 'float32x2', stride, offset: 0 },
        aCorner: { buffer: this.vertexBuffer, format: 'float32x2', stride, offset: 8 },
        aColor: { buffer: this.vertexBuffer, format: 'float32x3', stride, offset: 16 },
        aProgress: { buffer: this.vertexBuffer, format: 'float32', stride, offset: 28 },
        aThickness: { buffer: this.vertexBuffer, format: 'float32', stride, offset: 32 },
      },
      indexBuffer: indices,
    });

    const shader = Shader.from({ gl: { vertex, fragment } });
    const mesh = new Mesh({ geometry, shader, label: 'effects' });
    mesh.blendMode = 'add';
    this.container.addChild(mesh);
  }

  /**
   * Spawn one effect. `thickness` is a fraction of the radius for rings and is ignored
   * for bursts. `intensity` scales the colour past 1, which is what pushes an effect over
   * the bloom threshold and makes it read as light rather than as paint.
   */
  spawn(
    kind: EffectKind,
    x: number,
    y: number,
    radius: number,
    color: number,
    life: number,
    thickness = 0.16,
    intensity = 1.9,
  ): void {
    const effect = this.effects[this.next];
    this.next = (this.next + 1) % CAPACITY;

    effect.x = x;
    effect.y = y;
    effect.radius = radius;
    effect.age = 0;
    effect.life = Math.max(life, 0.016);
    effect.thickness = kind === 'burst' ? -1 : thickness;
    effect.r = (((color >> 16) & 0xff) / 255) * intensity;
    effect.g = (((color >> 8) & 0xff) / 255) * intensity;
    effect.b = ((color & 0xff) / 255) * intensity;
    effect.active = true;
  }

  clear(): void {
    for (const effect of this.effects) effect.active = false;
  }

  update(dt: number): void {
    const vertices = this.vertices;

    for (let i = 0; i < CAPACITY; i++) {
      const effect = this.effects[i];
      const base = i * 4 * VERTEX_FLOATS;

      if (effect.active) {
        effect.age += dt;
        if (effect.age >= effect.life) effect.active = false;
      }

      if (!effect.active) {
        // Collapse to a degenerate quad, which rasterises nothing.
        vertices.fill(0, base, base + 4 * VERTEX_FLOATS);
        continue;
      }

      // Ease out, so a ring leaves fast and settles rather than sweeping linearly.
      const t = effect.age / effect.life;
      const progress = 1 - (1 - t) * (1 - t);
      const radius = effect.radius;

      for (let corner = 0; corner < 4; corner++) {
        const cornerX = corner === 0 || corner === 3 ? -1 : 1;
        const cornerY = corner < 2 ? -1 : 1;
        const o = base + corner * VERTEX_FLOATS;
        vertices[o] = effect.x + cornerX * radius;
        vertices[o + 1] = effect.y + cornerY * radius;
        vertices[o + 2] = cornerX;
        vertices[o + 3] = cornerY;
        vertices[o + 4] = effect.r;
        vertices[o + 5] = effect.g;
        vertices[o + 6] = effect.b;
        vertices[o + 7] = progress;
        vertices[o + 8] = effect.thickness;
      }
    }

    this.vertexBuffer.update();
  }
}
